import {gql} from '@apollo/client';

import {
  CategoryKeyEnum,
  LearningResourceContext,
  LifestyleSummaryMostRecentLabSubmissionFragment,
  LifestyleSummaryMostRecentLabSubmissionWithResultsFragment,
  Maybe,
  useLifestyleSummary as useLifestyleSummaryQuery,
  useLifestyleSummaryEmpty as useLifestyleSummaryEmptyQuery,
} from '#apollo/schema';
import {learningResourceCardFragment} from '#component/partial/LearningResourceCard/LearningResourceCard';
import {SURVEY_KEY} from '#constant/survey';

export const lifestyleSummary = gql`
  ${learningResourceCardFragment}

  fragment LifestyleCategoryAssessmentFragment on CategoryAssessment {
    id
    categoryScore
    maxCategoryScore
    meetsRequirements
    category {
      id
      key
      description
      title
    }
    factorAssessments {
      id
      meetsRequirements
      healthFactor {
        id
        impactLevel
        recommendedResponse
        recommendationTitle
        title
      }
    }
  }

  fragment LifestyleSummaryMostRecentLabSubmissionFragment on LabSubmission {
    id
    registeredDate
    userSurveySubmission {
      id
      surveyAssessment {
        id
        lifestyleScore
        maxLifestyleScore
      }
    }
  }

  fragment LifestyleSummaryMostRecentLabSubmissionWithResultsFragment on LabSubmission {
    id
    registeredDate
    userSurveySubmission {
      id
      surveyAssessment {
        categories {
          id
          ...LifestyleCategoryAssessmentFragment
        }
        id
        lifestyleScore
        maxLifestyleScore
        surveyId
        surveyKey
        selectedActionPlan {
          id
        }
      }
    }
  }

  query LifestyleSummary($input: FindLearningResourcesInput!) {
    learningResources(input: $input) {
      ...LearningResourceCardFragment
    }
    me {
      id
      mostRecentLabSubmission {
        ...LifestyleSummaryMostRecentLabSubmissionFragment
      }
      mostRecentLabSubmissionWithResults {
        ...LifestyleSummaryMostRecentLabSubmissionWithResultsFragment
      }
      subscription {
        id
        hasActiveSubscription
        hasMembership
      }
    }
  }
`;

export const lifestyleSummaryEmpty = gql`
  query LifestyleSummaryEmpty($input: GetSurveyInput!) {
    survey(input: $input) {
      categories {
        id
        title
      }
      id
    }
  }
`;

const getShowLifestylePreview = (
  mostRecentLabSubmission?: Maybe<LifestyleSummaryMostRecentLabSubmissionFragment>,
  mostRecentLabSubmissionWithResults?: Maybe<LifestyleSummaryMostRecentLabSubmissionWithResultsFragment>,
) => {
  // If the user has never recieved results, show the preview if a
  // `labSubmission` exists _without_ an associated `labAssessment`, but
  // _with_ an associated `surveySubmission`.
  if (!mostRecentLabSubmissionWithResults) {
    return !!mostRecentLabSubmission?.userSurveySubmission;
  }

  // Otherwise, show the preview if a `labSubmission` that is not the
  // `mostRecentLabSubmissionWithResults` exists with an associated
  // `userSurveySubmission`.
  return (
    !!mostRecentLabSubmission &&
    mostRecentLabSubmissionWithResults.id !== mostRecentLabSubmission.id &&
    !!mostRecentLabSubmission.userSurveySubmission
  );
};

export const useLifestyleSummary = () => {
  const {data, loading, previousData} = useLifestyleSummaryQuery({
    variables: {input: {context: [LearningResourceContext.Lifestyle]}},
  });
  const fetchedData = data || previousData;
  const {data: emptyData, loading: emptyLoading} = useLifestyleSummaryEmptyQuery({
    skip: loading || !fetchedData || !!fetchedData.me.mostRecentLabSubmissionWithResults,
    variables: {input: {key: SURVEY_KEY}},
  });

  const {learningResources = [], me} = data || {};
  const {subscription, mostRecentLabSubmission, mostRecentLabSubmissionWithResults} = me || {};
  const {hasActiveSubscription, hasMembership} = subscription || {};
  const {id: submissionId, surveyAssessment} =
    mostRecentLabSubmissionWithResults?.userSurveySubmission || {};
  const {
    lifestyleScore,
    maxLifestyleScore = 100,
    categories = [],
    surveyId,
    surveyKey = null,
    selectedActionPlan,
  } = surveyAssessment || {};

  const filteredCategories = categories?.filter(
    category => category.category.key !== CategoryKeyEnum.You,
  );

  const categoriesMeetingRequirements = filteredCategories?.filter(
    assessment => assessment.meetsRequirements,
  );

  const categoriesNotMeetingRequirements = filteredCategories?.filter(
    assessment => !assessment.meetsRequirements,
  );

  const categoriesMeetingRequirementsTitles = categoriesMeetingRequirements?.map(
    assessment => assessment.category.title,
  );

  const categoriesNotMeetingRequirementsTitles = categoriesNotMeetingRequirements?.map(
    assessment => assessment.category.title,
  );

  const showLifestylePreview = getShowLifestylePreview(
    mostRecentLabSubmission,
    mostRecentLabSubmissionWithResults,
  );

  const surveyCategories = (
    emptyData?.survey?.categories ||
    filteredCategories?.map(({category}) => category) ||
    []
  ).map(category => category.title);

  return {
    categories: filteredCategories,
    categoriesMeetingRequirementsTitles,
    categoriesNotMeetingRequirementsTitles,
    hasActiveSubscription: !!hasActiveSubscription,
    hasMembership: !!hasMembership,
    hasCompletedSurveyForMostRecentLabSubmissionWithResults:
      !!mostRecentLabSubmissionWithResults?.userSurveySubmission?.id,
    hasSelectedActionPlan: !!selectedActionPlan?.id,
    learningResources,
    lifestyleScore: lifestyleScore || 0,
    loading: loading || emptyLoading,
    maxLifestyleScore,
    mostRecentLabSubmission,
    mostRecentLabSubmissionWithResults,
    showLifestylePreview,
    submissionId,
    surveyCategories,
    surveyId,
    surveyKey,
  };
};
